<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@lib/components/shared/PageHeader.vue'
import EmptyState from '@lib/components/shared/EmptyState.vue'
import SkeletonRows from '@lib/components/shared/SkeletonRows.vue'
import ExpandRowPanel from '@lib/components/shared/ExpandRowPanel.vue'
import BInput from '@lib/components/base/BInput.vue'
import { ClipboardPlus } from 'lucide-vue-next'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@lib/composables/useToast'
import { useZonesData, type Zone } from '@/composables/useZonesData'
import { useClipboardStore } from '@/stores/clipboard'
import ZoneEditPanel from '@/components/domain/zones/ZoneEditPanel.vue'

const { t } = useI18n()
const ui = useUiStore()
const auth = useAuthStore()
const toast = useToast()
const clipboard = useClipboardStore()
const data = useZonesData()

const expandedId = ref<number | null>(null)

function toggle(id: number) {
    expandedId.value = expandedId.value === id ? null : id
}

function addToClipboard(zone: Zone, ev?: Event) {
    ev?.stopPropagation()
    clipboard.add({
        kind: 'zones',
        item: { id: zone.id, name: zone.name }
    })
    toast.success(t('zones.qr.added'))
}

onMounted(async () => {
    ui.setBreadcrumbs([{ label: t('zones.list.title') }])
    await data.refetch()
})

watch(
    () => auth.invalidationKey,
    () => {
        expandedId.value = null
        data.refetch()
    }
)
</script>

<template>
    <DefaultLayout>
        <PageHeader :title="t('zones.list.title')">
            <template #actions>
                <BInput
                    v-model="data.filters.value.search"
                    :placeholder="t('zones.list.search')"
                    class="w-64"
                />
            </template>
        </PageHeader>

        <div class="bg-white border border-gray-200 rounded-12 overflow-hidden">
            <SkeletonRows v-if="data.loading.value && !data.filteredZones.value.length" :rows="8" :columns="2" />
            <EmptyState v-else-if="!data.loading.value && !data.filteredZones.value.length" :title="t('zones.list.empty')" />
            <template v-else>
                <div class="divide-y divide-gray-100">
                    <template v-for="zone in data.filteredZones.value" :key="zone.id">
                        <div
                            class="px-4 py-3 flex items-center gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                            @click="toggle(zone.id)"
                        >
                            <div class="flex-1 min-w-0">
                                <div class="font-medium text-ink truncate">{{ zone.name }}</div>
                                <div class="text-xs text-gray-500">
                                    ID {{ zone.id }} · {{ t('zones.fields.slots') }}: {{ zone.slots.length }}
                                </div>
                            </div>
                            <button
                                type="button"
                                class="text-gray-500 hover:text-teal transition-colors shrink-0 p-1"
                                :title="t('zones.qr.addToClipboard')"
                                @click="(ev: Event) => addToClipboard(zone, ev)"
                            >
                                <ClipboardPlus class="w-4 h-4" />
                            </button>
                        </div>
                        <ExpandRowPanel
                            v-if="expandedId === zone.id"
                            :title="zone.name"
                            @close="toggle(zone.id)"
                        >
                            <ZoneEditPanel :zone="zone" />
                        </ExpandRowPanel>
                    </template>
                </div>
            </template>
        </div>
    </DefaultLayout>
</template>
